// src/context/CartProvider.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import CartContext from './CartContext';
import PropTypes from 'prop-types';
import { NODE_BACKEND_API_URL } from '../config';
import { calculateCartV2 } from '../services/invoiceService';
import { useDualCalculation } from '../hooks/useDualCalculation';

// --- Constants ---
const CART_DETAIL_API_URL = `${NODE_BACKEND_API_URL}/postidcart`;
const LOCAL_STORAGE_CART_KEY = 'appCartWithQuantities';
const LOCAL_STORAGE_DISCOUNT_KEY = 'appCartDiscountPercentage';
const LOCAL_STORAGE_VOLUME_PRICING_KEY = 'appCartVolumePricing';
const LOCAL_STORAGE_AUTH_KEY = 'authUser'; // Using localStorage for authUser
// GIULIANO feature: key del switch "Precio Credito" se scope por organizationId
// para evitar leak entre organizaciones (PM con acceso multi-org).
const LOCAL_STORAGE_CREDIT_PRICE_KEY = (orgId) => `appCartUseCreditCardPrice_${orgId || 'unknown'}`;

// More robust function to round to a specific number of decimal places
// This uses toFixed() which is generally more reliable for decimal precision
const roundToDecimals = (num, decimals) => {
    if (typeof num !== 'number' || isNaN(num)) {
        return 0; // Return 0 or handle error as appropriate
    }
    // Convert to string with fixed decimals, then parse back to float
    // This helps bypass typical floating-point arithmetic issues for storage/display
    return parseFloat(num.toFixed(decimals));
};

const CartProvider = ({ children }) => {
    // --- State for Cart (Centralized) ---
    const [cartItems, setCartItems] = useState([]);
    const [cartDetails, setCartDetails] = useState([]);
    const [isLoadingCart, setIsLoadingCart] = useState(false);
    const [lastFetchedProductIds, setLastFetchedProductIds] = useState(''); // Track which products we have details for
    const [discountPercentage, setDiscountPercentageState] = useState(() => {
        try {
            const savedDiscount = localStorage.getItem(LOCAL_STORAGE_DISCOUNT_KEY);
            const parsedDiscount = savedDiscount ? parseInt(savedDiscount, 10) : 0;
            // Ensure it's a number between 0 and 100
            return Math.min(100, Math.max(0, isNaN(parsedDiscount) ? 0 : parsedDiscount));
        } catch (e) {
            console.error("Error loading discount from localStorage:", e);
            return 0;
        }
    });

    // --- State for Volume-Based Pricing ---
    const [volumePricingStates, setVolumePricingStates] = useState(() => {
        try {
            const savedVolumePricing = localStorage.getItem(LOCAL_STORAGE_VOLUME_PRICING_KEY);
            return savedVolumePricing ? JSON.parse(savedVolumePricing) : {};
        } catch (e) {
            console.error("Error loading volume pricing from localStorage:", e);
            return {};
        }
    });
    const [volumePricingVersion, setVolumePricingVersion] = useState(0);

    // --- State for Volume Pricing Configuration from Backend ---
    // ⚠️ IMPORTANT: These defaults MUST match database values in dbo.VolumeBased
    // Database: RangeValue1=20, MarkValue1=100, RangeValue2=100, MarkValue2=65
    const [volumePricingConfig, setVolumePricingConfig] = useState({
        rangeValue1: 20,   // Tier 1 max quantity (1-20 units)
        markValue1: 100,   // Tier 1 markup: +100% (double price)
        rangeValue2: 100,  // Tier 2 max quantity (21-100 units)
        markValue2: 65     // Tier 2 markup: +65%
        // Tier 3 (101+ units) has 0% markup (wholesale)
    });

    // --- State to store authUser info for conditional logic ---
    const [clientDatabaseName, setClientDatabaseName] = useState(() => {
        console.log("CartProvider: Initializing clientDatabaseName from localStorage synchronously.");
        try {
            const authUserString = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
            if (authUserString) {
                const authUser = JSON.parse(authUserString);
                console.log("CartProvider: Synchronous authUser object:", authUser);
                console.log("CartProvider: clientDatabaseName value:", authUser?.clientDatabaseName);
                if (authUser?.clientDatabaseName) {
                    return authUser.clientDatabaseName;
                }
            } else {
                console.warn("⚠️ CartProvider: No authUser found in localStorage");
            }
        } catch (e) {
            console.error("CartProvider: Error initializing clientDatabaseName from localStorage:", e);
        }
        
        // Fallback to DUTTO_TEST to enable V2 calculations
        console.log("CartProvider: Using fallback clientDatabaseName: DUTTO_TEST");
        return "DUTTO_TEST";
    });

    // --- DUAL CALCULATION HOOK (V3) - Moved to after getDisplayRetailPrice is defined ---
    // Will be initialized later after all necessary functions are defined

    // Effect to handle storage events for authUser (for cross-tab sync)
    useEffect(() => {
        console.log("CartProvider: useEffect for authUser storage event listener running.");

        const handleAuthStorageEvent = (event) => {
            if (event.key === LOCAL_STORAGE_AUTH_KEY) {
                console.log("CartProvider: Storage event detected for authUser.");
                try {
                    if (event.newValue) {
                        const updatedAuthUser = JSON.parse(event.newValue);
                        setClientDatabaseName(updatedAuthUser?.clientDatabaseName || null);
                        console.log("CartProvider: clientDatabaseName updated from storage event to:", updatedAuthUser?.clientDatabaseName);
                    } else {
                        setClientDatabaseName(null);
                        console.log("CartProvider: authUser cleared from storage event.");
                    }
                } catch (e) {
                    console.error("CartProvider: Error parsing authUser from storage event:", e);
                    setClientDatabaseName(null);
                }
            }
        };

        window.addEventListener('storage', handleAuthStorageEvent);

        return () => {
            window.removeEventListener('storage', handleAuthStorageEvent);
        };
    }, []);


    // --- Helper function to load cart from localStorage (updated) ---
    const loadCartFromLocalStorage = useCallback(() => {
        console.log("CartProvider: Attempting to load cart from localStorage...");
        try {
            const savedCart = localStorage.getItem(LOCAL_STORAGE_CART_KEY);
            if (savedCart) {
                const parsedCart = JSON.parse(savedCart);
                // Validate parsedCart to ensure it's an array of objects with productId, quantity, and optional splitter
                if (Array.isArray(parsedCart) && parsedCart.every(item =>
                    typeof item === 'object' && item !== null &&
                    typeof item.productId !== 'undefined' &&
                    typeof item.quantity === 'number' && item.quantity > 0 && // Quantity check still good for floats
                    (typeof item.splitter === 'undefined' || (typeof item.splitter === 'number' && item.splitter >= 1 && item.splitter <= 9999))
                )) {
                    console.log("CartProvider: Loaded cart from localStorage:", parsedCart);
                    // Ensure loaded quantities are rounded too to maintain precision
                    setCartItems(parsedCart.map(item => ({
                        ...item,
                        quantity: roundToDecimals(item.quantity, 2), // Apply rounding on load
                        splitter: item.splitter || 1
                    })));
                } else {
                    console.error("CartProvider: Invalid cart data format in localStorage. Clearing.");
                    localStorage.removeItem(LOCAL_STORAGE_CART_KEY);
                    setCartItems([]);
                }
            } else {
                console.log("CartProvider: No cart found in localStorage.");
                setCartItems([]);
            }
        } catch (e) {
            console.error("CartProvider: Error loading cart from localStorage:", e);
            localStorage.removeItem(LOCAL_STORAGE_CART_KEY);
            setCartItems([]);
        }
    }, []);

    // --- Helper function to save cart to localStorage (updated) ---
    const saveCartToLocalStorage = useCallback((cartData) => {
        console.log("CartProvider: Saving cart to localStorage:", cartData);
        try {
            localStorage.setItem(LOCAL_STORAGE_CART_KEY, JSON.stringify(cartData));
            window.dispatchEvent(new Event('cartStateUpdated'));
        } catch (e) {
            console.error("CartProvider: Error saving cart to localStorage:", e);
        }
    }, []);

    // --- Helper function to save discount to localStorage (unchanged) ---
    const saveDiscountToLocalStorage = useCallback((discountData) => {
        console.log("CartProvider: Saving discount to localStorage:", discountData);
        try {
            localStorage.setItem(LOCAL_STORAGE_DISCOUNT_KEY, discountData.toString());
        } catch (e) {
            console.error("CartProvider: Error saving discount to localStorage:", e);
        }
    }, []);

    // --- Helper function to save volume pricing to localStorage ---
    const saveVolumePricingToLocalStorage = useCallback((volumePricingData) => {
        console.log("CartProvider: Saving volume pricing to localStorage:", volumePricingData);
        try {
            localStorage.setItem(LOCAL_STORAGE_VOLUME_PRICING_KEY, JSON.stringify(volumePricingData));
        } catch (e) {
            console.error("CartProvider: Error saving volume pricing to localStorage:", e);
        }
    }, []);

    // --- Helper function to fetch volume pricing configuration from backend ---
    const fetchVolumePricingConfig = useCallback(async () => {
        try {
            console.log("📡 [Volume Config] Fetching from backend...");
            const response = await axios.get(`${NODE_BACKEND_API_URL}/getvolumebasedprices/`);
            console.log("📡 [Volume Config] Backend response:", response.data);
            
            if (response.data && typeof response.data === 'object') {
                const config = {
                    // Use database values from backend, fallback to correct defaults if missing
                    rangeValue1: response.data.RangeValue1 || 20,
                    markValue1: response.data.MarkValue1 || 100,
                    rangeValue2: response.data.RangeValue2 || 100,
                    markValue2: response.data.MarkValue2 || 65
                };
                console.log("✅ [Volume Config] Loaded from database:", {
                    tier1: `1-${config.rangeValue1} units: +${config.markValue1}% markup`,
                    tier2: `${config.rangeValue1+1}-${config.rangeValue2} units: +${config.markValue2}% markup`,
                    tier3: `${config.rangeValue2+1}+ units: 0% markup (wholesale)`
                });
                setVolumePricingConfig(config);
            } else {
                console.warn("⚠️ [Volume Config] Invalid response format, using defaults");
            }
        } catch (error) {
            console.error("❌ [Volume Config] Error fetching from backend:", error);
            console.log("📦 [Volume Config] Using default values from state");
            // Keep default values on error (already set in useState)
        }
    }, []);

    // --- Effect to load cart on provider mount (updated to include discount) ---
    useEffect(() => {
        loadCartFromLocalStorage();
        fetchVolumePricingConfig(); // Fetch volume pricing configuration

        const handleStorageEvent = (event) => {
            if (event.key === LOCAL_STORAGE_CART_KEY && event.newValue !== event.oldValue) {
                console.log("CartProvider: Storage event detected (from other tab) for cart, reloading cart state.");
                if (event.newValue) {
                    loadCartFromLocalStorage();
                } else {
                    setCartItems([]);
                }
            }
            if (event.key === LOCAL_STORAGE_DISCOUNT_KEY && event.newValue !== event.oldValue) {
                console.log("CartProvider: Storage event detected (from other tab) for discount, reloading discount state.");
                const parsedDiscount = event.newValue ? parseInt(event.newValue, 10) : 0;
                setDiscountPercentageState(Math.min(100, Math.max(0, isNaN(parsedDiscount) ? 0 : parsedDiscount)));
            }
        };

        window.addEventListener('storage', handleStorageEvent);

        return () => {
            window.removeEventListener('storage', handleStorageEvent);
        };
    }, [loadCartFromLocalStorage, fetchVolumePricingConfig]);


    // --- Effect to fetch cart item details when cartItems state changes (OPTIMIZED) ---

    useEffect(() => {
        const fetchCartDetails = async () => {
            // Create a sorted string of product IDs to detect if the actual products changed
            const currentProductIdsString = cartItems
                .map(item => item.productId)
                .sort()
                .join(',');

            // If cart is empty, clear details
            if (cartItems.length === 0) {
                console.log("CartProvider: cartItems state is empty, clearing details.");
                setCartDetails([]);
                setLastFetchedProductIds('');
                setIsLoadingCart(false);
                return;
            }

            // OPTIMIZATION: Check if only quantity/splitter changed (not products added/removed)
            if (lastFetchedProductIds === currentProductIdsString && cartDetails.length > 0) {
                console.log("CartProvider: Product list unchanged, updating only quantity/splitter locally (no backend fetch).");
                
                // Update cartDetails with new quantity/splitter without fetching from backend
                const updatedCartDetails = cartDetails.map(detail => {
                    const cartItem = cartItems.find(item => item.productId === detail.productId);
                    if (cartItem) {
                        return {
                            ...detail,
                            quantity: roundToDecimals(cartItem.quantity, 2),
                            splitter: cartItem.splitter || 1
                        };
                    }
                    return detail;
                });
                
                setCartDetails(updatedCartDetails);
                return; // Skip backend fetch
            }

            // Products were added/removed, fetch from backend
            console.log("CartProvider: Product list changed, fetching details from backend.");
            setIsLoadingCart(true);

            // Filter out custom products (negative IDs) from backend fetch
            const productIdsForBackend = cartItems
                .filter(item => item.productId > 0) // Only positive IDs (real products)
                .map(item => {
                    if (item.productId != null) {
                        return { ProductId: item.productId };
                    }
                    return null;
                })
                .filter(item => item !== null);

            try {
                // Only fetch if there are real products (positive IDs) to fetch
                if (productIdsForBackend.length === 0) {
                    // Only custom products in cart, no backend fetch needed
                    console.log("CartProvider: Only custom products in cart, skipping backend fetch.");
                    
                    const customProductsOnly = cartItems
                        .filter(cartItem => cartItem.productId < 0 && cartItem.isCustom)
                        .map(cartItem => {
                            // Find existing custom product details
                            const existingCustom = cartDetails.find(detail => detail.productId === cartItem.productId);
                            if (existingCustom) {
                                return {
                                    ...existingCustom,
                                    quantity: roundToDecimals(cartItem.quantity, 2),
                                    splitter: cartItem.splitter || 1,
                                };
                            }
                            return null;
                        })
                        .filter(item => item !== null);
                    
                    setCartDetails(customProductsOnly);
                    setLastFetchedProductIds(currentProductIdsString);
                    setIsLoadingCart(false);
                    return;
                }

                const response = await axios.post(CART_DETAIL_API_URL, { productIds: productIdsForBackend });

                if (response.data && Array.isArray(response.data.dataresponse)) {
                    const fetchedDetails = response.data.dataresponse;
                    console.log("CartProvider: Fetched cart details response:", fetchedDetails);

                    const mergedCartData = cartItems
                        .map(cartItem => {
                            // Handle custom products (negative IDs)
                            if (cartItem.productId < 0 && cartItem.isCustom) {
                                const existingCustom = cartDetails.find(detail => detail.productId === cartItem.productId);
                                if (existingCustom) {
                                    return {
                                        ...existingCustom,
                                        quantity: roundToDecimals(cartItem.quantity, 2),
                                        splitter: cartItem.splitter || 1,
                                    };
                                }
                                console.warn(`CartProvider: Custom product ${cartItem.productId} not found in existing details.`);
                                return null;
                            }
                            
                            // Handle regular products from backend
                            const detail = fetchedDetails.find(fetchedItem => fetchedItem.ProductId === cartItem.productId);
                            if (detail) {
                                return {
                                    ...detail,
                                    productId: cartItem.productId,
                                    quantity: roundToDecimals(cartItem.quantity, 2),
                                    splitter: cartItem.splitter || 1,
                                };
                            }
                            console.warn(`CartProvider: Details not found for productId: ${cartItem.productId}. This item will not be displayed.`);
                            return null;
                        })
                        .filter(item => item !== null);

                    setCartDetails(mergedCartData);
                    setLastFetchedProductIds(currentProductIdsString); // Remember which products we fetched

                } else {
                    console.error("CartProvider: API response for cart details format is incorrect:", response.data);
                    setCartDetails([]);
                }

            } catch (error) {
                console.error('CartProvider: Error fetching cart details:', error);
                setCartDetails([]);
            } finally {
                setIsLoadingCart(false);
            }
        };

        fetchCartDetails();

    }, [cartItems, lastFetchedProductIds]); // cartDetails removed to prevent infinite loop


    // --- Derived State for Cart Count (unchanged) ---
    const totalCartItemsCount = useMemo(() => {
        return cartItems.reduce((total, item) => total + item.quantity, 0);
    }, [cartItems]);


    // --- Price Display Function (rounding now handled by ProductCostRounded column in SQL) ---
    const getDisplayRetailPrice = useCallback((originalPrice) => {
        const price = parseFloat(originalPrice);
        if (isNaN(price)) {
            return 0;
        }
        return price;
    }, []);

    // --- GIULIANO feature: Toggle "Precio Credito" (markup tarjeta) ---
    // Estado del switch del carrito. Default OFF. Persistido por organizationId.
    const [useCreditCardPrice, setUseCreditCardPriceState] = useState(() => {
        try {
            const authUserString = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
            if (!authUserString) return false;
            const authUser = JSON.parse(authUserString);
            const orgId = authUser?.organizationId;
            const saved = localStorage.getItem(LOCAL_STORAGE_CREDIT_PRICE_KEY(orgId));
            return saved === 'true';
        } catch (e) {
            return false;
        }
    });

    // Effect: reset del toggle cuando cambia la organizacion (evita leak entre orgs para PM multi-org)
    useEffect(() => {
        const authUserString = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
        let savedOrgId = null;
        try {
            const authUser = authUserString ? JSON.parse(authUserString) : null;
            savedOrgId = authUser?.organizationId;
        } catch (e) {
            savedOrgId = null;
        }
        const newVal = localStorage.getItem(LOCAL_STORAGE_CREDIT_PRICE_KEY(savedOrgId)) === 'true';
        setUseCreditCardPriceState(newVal);
    }, [clientDatabaseName]);

    const setUseCreditCardPrice = useCallback((value) => {
        setUseCreditCardPriceState(value);
        try {
            const authUserString = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
            const authUser = authUserString ? JSON.parse(authUserString) : null;
            const orgId = authUser?.organizationId;
            localStorage.setItem(LOCAL_STORAGE_CREDIT_PRICE_KEY(orgId), value ? 'true' : 'false');
        } catch (e) {
            console.error('CartProvider: Error saving useCreditCardPrice:', e);
        }
    }, []);

    // Helper central: precio efectivo del item segun toggle.
    // TEMA CLAVE: NO se asigna al item (no se muta). Se deriva siempre.
    // Cuando useCreditCardPrice=true y el item tiene RetailCardPrice (>0), usa ese.
    // Caso contrario usa RetailPrice (ya viene redondeado del backend cuando hay step activo).
    const getEffectivePrice = useCallback((item) => {
        if (!item) return 0;
        const basePrice = parseFloat(item.RetailPrice);
        if (!useCreditCardPrice) return basePrice || 0;
        const cardPrice = parseFloat(item.RetailCardPrice);
        if (cardPrice && cardPrice > 0) return cardPrice;
        // Fallback: RetailPrice si RetailCardPrice es NULL (caso edge)
        if (typeof cardPrice === 'number' && cardPrice === 0) {
            console.warn(`[CreditCardMarkup] RetailCardPrice=0 para ProductId=${item.ProductId}, fallback a RetailPrice`);
        } else {
            console.warn(`[CreditCardMarkup] RetailCardPrice NULL para ProductId=${item.ProductId}, fallback a RetailPrice`);
        }
        return basePrice || 0;
    }, [useCreditCardPrice]);


    // --- Calculate Subtotal (updated to include volume pricing and splitter) ---
    const subTotalPrice = useMemo(() => {
        // console.log("CartProvider: Recalculating subTotalPrice...");
        return cartDetails.reduce((sum, item) => {
            const basePrice = getEffectivePrice(item);
            
            // Check if volume pricing is active for this item
            const isVolumeActive = volumePricingStates[item.productId] || false;
            let effectivePrice = basePrice;
            
            if (isVolumeActive) {
                // Apply volume pricing using the same logic as getVolumePrice
                const quantity = item.quantity;
                const { rangeValue1, markValue1, rangeValue2, markValue2 } = volumePricingConfig;

                if (quantity >= 0.01 && quantity <= rangeValue1) {
                    // Range 1: Apply markValue1
                    const markup = markValue1 / 100;
                    effectivePrice = basePrice * (1 + markup);
                } else if (quantity >= (rangeValue1 + 1) && quantity <= rangeValue2) {
                    // Range 2: Apply markValue2
                    const markup = markValue2 / 100;
                    effectivePrice = basePrice * (1 + markup);
                } else {
                    // More than rangeValue2: Use retail price (no markup)
                    effectivePrice = basePrice;
                }
            }
            
            // Quantity can now be a decimal
            const itemQuantity = typeof item.quantity === 'number' ? item.quantity : 0;
            const itemSplitter = typeof item.splitter === 'number' && item.splitter > 0 ? item.splitter : 1; // Get splitter, default to 1

            if (!isNaN(effectivePrice) && itemQuantity > 0) {
                // Apply splitter division *before* multiplying by quantity
                const pricePerUnitAfterSplit = effectivePrice / itemSplitter;
                // Round the total for this item to ensure precise subtotal calculation
                return sum + roundToDecimals((pricePerUnitAfterSplit * itemQuantity), 2); // Round each item's total
            }
            return sum;
        }, 0);
    }, [cartDetails, getDisplayRetailPrice, getEffectivePrice, volumePricingStates, volumePricingConfig]);


    // --- Calculate Total after Discount (FIXED: TusFacturas method) ---
    const totalAfterDiscount = useMemo(() => {
        if (discountPercentage === 0) {
            return roundToDecimals(subTotalPrice, 2);
        }

        // AFIP-Compliant Calculation Method:
        // 1. Calculate base prices WITHOUT IVA
        // 2. Apply discount to base (sin IVA)
        // 3. Calculate IVA on discounted base
        // 4. Total = Discounted base + IVA
        
        // Calculate total base price WITHOUT IVA and weighted IVA rate
        let totalBaseWithoutDiscount = 0;
        let weightedIVASum = 0;
        const itemBases = [];
        
        cartDetails.forEach(item => {
            const basePrice = getEffectivePrice(item);
            
            // ⚡ Apply volume pricing if active (same logic as subTotalPrice)
            const isVolumeActive = volumePricingStates[item.productId] || false;
            let effectivePrice = basePrice;
            
            if (isVolumeActive) {
                const quantity = item.quantity;
                const { rangeValue1, markValue1, rangeValue2, markValue2 } = volumePricingConfig;

                if (quantity >= 0.01 && quantity <= rangeValue1) {
                    // Range 1: Apply markValue1
                    const markup = markValue1 / 100;
                    effectivePrice = basePrice * (1 + markup);
                } else if (quantity >= (rangeValue1 + 1) && quantity <= rangeValue2) {
                    // Range 2: Apply markValue2
                    const markup = markValue2 / 100;
                    effectivePrice = basePrice * (1 + markup);
                } else {
                    // More than rangeValue2: Use retail price (no markup)
                    effectivePrice = basePrice;
                }
            }
            
            const itemQuantity = roundToDecimals(item.quantity, 2);
            const itemSplitter = item.splitter || 1;
            const pricePerUnitAfterSplit = effectivePrice / itemSplitter;
            
            const ivaRate = item.IVARate || item.alicuota || 21;
            const ivaMultiplier = ivaRate / 100;
            
            const itemTotalWithIVA = pricePerUnitAfterSplit * itemQuantity;
            const itemBasePrice = itemTotalWithIVA / (1 + ivaMultiplier);
            
            totalBaseWithoutDiscount += itemBasePrice;
            weightedIVASum += itemBasePrice * ivaMultiplier;
            itemBases.push({ itemBasePrice, ivaMultiplier, itemTotalWithIVA });
        });
        
        // Calculate weighted average IVA rate
        const avgIVAMultiplier = totalBaseWithoutDiscount > 0 
            ? weightedIVASum / totalBaseWithoutDiscount 
            : 0.21;
        
        // ✅ AFIP-COMPLIANT: Apply discount to BASE PRICE (sin IVA)
        const discountAmountOnBase = totalBaseWithoutDiscount * (discountPercentage / 100);
        
        // Apply discount to base price
        const totalBaseAfterDiscount = totalBaseWithoutDiscount - discountAmountOnBase;
        
        // Calculate IVA on discounted base for each item proportionally
        let totalIVAAfterDiscount = 0;
        itemBases.forEach(({ itemBasePrice, ivaMultiplier }) => {
            // Each item's proportion of the total base
            const itemProportion = itemBasePrice / totalBaseWithoutDiscount;
            // Apply that proportion to the discounted base
            const itemDiscountedBase = totalBaseAfterDiscount * itemProportion;
            // Calculate IVA on this item's discounted base
            const itemIVA = itemDiscountedBase * ivaMultiplier;
            totalIVAAfterDiscount += itemIVA;
        });
        
        // Final total = Discounted base + IVA
        const finalTotal = totalBaseAfterDiscount + totalIVAAfterDiscount;
        
        console.log('💰 AFIP-Compliant Discount Calculation:', {
            originalSubtotal: subTotalPrice.toFixed(2),
            discountPercentage: `${discountPercentage}%`,
            totalBaseWithoutDiscount: totalBaseWithoutDiscount.toFixed(2),
            discountAmountOnBase: discountAmountOnBase.toFixed(2),
            avgIVARate: `${(avgIVAMultiplier * 100).toFixed(2)}%`,
            totalBaseAfterDiscount: totalBaseAfterDiscount.toFixed(2),
            ivaOnDiscountedBase: totalIVAAfterDiscount.toFixed(2),
            finalTotal: finalTotal.toFixed(2),
            savedAmount: (subTotalPrice - finalTotal).toFixed(2)
        });
        
        return roundToDecimals(finalTotal, 2);
    }, [subTotalPrice, discountPercentage, cartDetails, getEffectivePrice, volumePricingStates, volumePricingConfig]);

    // --- DUAL CALCULATION HOOK (V3) ---
    // Called here after getDisplayRetailPrice is defined
    const dualCalculation = useDualCalculation({
        cartDetails,
        discountPercentage,
        clientDatabaseName,
        invoiceType: 'C',
        enabled: cartDetails.length > 0 && !!clientDatabaseName,
        debounceDelay: 150, // User specified: 150ms
        validationThreshold: 10.0, // User specified: $10.0
        // Volume pricing support
        getDisplayRetailPrice,
        isVolumePricingActive: (productId) => volumePricingStates[productId] || false,
        getVolumePrice: (retailPrice, productId, quantity) => {
            // Don't check volume state here - caller already checked using ref
            // This function just calculates the markup based on quantity
            
            if (!quantity) return retailPrice;
            
            const { rangeValue1, markValue1, rangeValue2, markValue2 } = volumePricingConfig;
            
            if (quantity >= 0.01 && quantity <= rangeValue1) {
                return retailPrice * (1 + markValue1 / 100);
            } else if (quantity >= (rangeValue1 + 1) && quantity <= rangeValue2) {
                return retailPrice * (1 + markValue2 / 100);
            } else {
                return retailPrice;
            }
        },
        volumePricingStates,
        volumePricingConfig,
        volumePricingVersion
    });

    // --- LEGACY V2 State (kept for backwards compatibility) ---
    // Note: These are now derived from dualCalculation
    const v2Calculation = dualCalculation.backendCalculation;
    const isCalculatingV2 = dualCalculation.isValidating;
    const v2Error = dualCalculation.validationError;

    // --- Manual calculation function (triggers immediate validation) ---
    const calculateCart = useCallback(() => {
        if (!cartDetails || cartDetails.length === 0 || !clientDatabaseName) {
            console.warn('⚠️ Cannot calculate: empty cart or no database');
            return;
        }

        console.log('📞 Manual calculation triggered (DUAL mode)');
        dualCalculation.recalculate();
    }, [cartDetails, clientDatabaseName, dualCalculation]);

    // --- Get Discount Breakdown (V2 ONLY - NO FALLBACK) ---
    // Now powered by React Query - no manual useEffect needed!
    const getDiscountBreakdown = useCallback(() => {
        // Check if volume pricing is active for any item
        const hasVolumePricing = Object.values(volumePricingStates).some(state => state === true);
        
        console.log('📊 [getDiscountBreakdown] Volume pricing active?', hasVolumePricing, 
                    'Mismatch?', dualCalculation?.mismatchDetected,
                    'Frontend calc?', !!dualCalculation?.frontendCalculation);
        
        // V2 calculation is MANDATORY
        if (v2Calculation) {
            console.log('📊 [React Query] Using backend calculation for breakdown', {
                hasVolumePricing,
                hasMismatch: dualCalculation?.mismatchDetected
            });
            
            // Backend now supports volume pricing! Use it normally.
            // Only override if there's an actual mismatch detected by the dual calculation system
            return {
                subtotalWithIVA: parseFloat(v2Calculation.subtotalSinIva) * 1.21,
                subtotalWithoutIVA: parseFloat(v2Calculation.subtotalSinIva),
                discountAmount: parseFloat(v2Calculation.discount),
                discountAmountSimple: parseFloat(v2Calculation.discount) / 1.21, // For display
                subtotalAfterDiscount: parseFloat(v2Calculation.subtotalAfterDiscount),
                ivaAmount: parseFloat(v2Calculation.iva),
                finalTotal: parseFloat(v2Calculation.total),
                calculationSource: 'backend-v2',
                isCalculating: isCalculatingV2,
                error: v2Error,
                timestamp: new Date().toISOString()
            };
        }
        
        // No V2 calculation available yet - use frontend calculation as temporary fallback
        if (dualCalculation?.frontendCalculation) {
            console.log('📊 Using frontend calculation while waiting for backend validation');
            const frontendCalc = dualCalculation.frontendCalculation;
            return {
                subtotalWithIVA: parseFloat(frontendCalc.subtotalSinIva) * 1.21,
                subtotalWithoutIVA: parseFloat(frontendCalc.subtotalSinIva),
                discountAmount: parseFloat(frontendCalc.discount) * 1.21,
                discountAmountSimple: parseFloat(frontendCalc.discount),
                subtotalAfterDiscount: parseFloat(frontendCalc.subtotalAfterDiscount),
                ivaAmount: parseFloat(frontendCalc.iva),
                finalTotal: parseFloat(frontendCalc.total),
                calculationSource: 'frontend-instant',
                isCalculating: dualCalculation.isValidating,
                error: null,
                timestamp: new Date().toISOString()
            };
        }
        
        // No calculation available at all - show loading state
        console.warn('⚠️ No calculation available - waiting for backend');
        
        // Return loading/error state instead of V1 calculation
        return {
            subtotalWithIVA: subTotalPrice,
            subtotalWithoutIVA: 0,
            discountAmount: 0,
            discountAmountSimple: 0,
            subtotalAfterDiscount: subTotalPrice,
            ivaAmount: 0,
            finalTotal: subTotalPrice,
            calculationSource: 'backend-v2', // Still V2, just not loaded yet
            isCalculating: isCalculatingV2,
            error: v2Error || 'Esperando cálculo del servidor...',
            timestamp: null
        };
    }, [v2Calculation, isCalculatingV2, v2Error, subTotalPrice, volumePricingStates, dualCalculation, totalAfterDiscount]);


    // --- Cart Manipulation Handlers (updated for splitter) ---

    // Toggle an item's presence in the cart (add or remove if exists)
    const toggleItemInCart = useCallback((productId, isSelected) => {
        setCartItems(currentCartItems => {
            const itemIndex = currentCartItems.findIndex(item => item.productId === productId);
            let newCartItems;

            if (isSelected) {
                if (itemIndex === -1) {
                    // When adding, default quantity to 1 (integer)
                    newCartItems = [...currentCartItems, { productId: productId, quantity: 1, splitter: 1 }];
                    console.log(`CartProvider: Added productId ${productId} to cart with quantity 1.`);
                } else {
                    // If item already exists and its quantity is non-positive (e.g., 0 or less, which shouldn't happen with filtering)
                    // ensure it becomes 1. This handles edge cases where an item might exist with a zero quantity.
                    if (currentCartItems[itemIndex].quantity <= 0) {
                        newCartItems = currentCartItems.map(item =>
                            item.productId === productId ? { ...item, quantity: 1 } : item
                        );
                        console.log(`CartProvider: Ensured quantity is 1 for existing productId ${productId}.`);
                    } else {
                        newCartItems = currentCartItems;
                        console.log(`CartProvider: ProductId ${productId} already in cart with positive quantity.`);
                    }
                }
            } else {
                if (itemIndex !== -1) {
                    newCartItems = currentCartItems.filter(item => item.productId !== productId);
                    console.log(`CartProvider: Removed productId ${productId} from cart.`);
                } else {
                    newCartItems = currentCartItems;
                    console.log(`CartProvider: ProductId ${productId} not found in cart.`);
                }
            }

            saveCartToLocalStorage(newCartItems);
            return newCartItems;
        });
    }, [saveCartToLocalStorage]);


    // Remove an item completely from the cart (unchanged)
    const removeItemFromCart = useCallback((productId) => {
        setCartItems(currentCartItems => {
            const newCartItems = currentCartItems.filter(item => item.productId !== productId);
            console.log(`CartProvider: Removing productId ${productId} from cart.`);
            saveCartToLocalStorage(newCartItems);
            return newCartItems;
        });
    }, [saveCartToLocalStorage]);


    // Update the quantity of an item in the cart to a new (potentially decimal) value
    const updateItemQuantity = useCallback((productId, newQuantityValue) => {
        setCartItems(currentCartItems => {
            const newCartItems = currentCartItems.map(item => {
                if (item.productId === productId) {
                    console.log(`CartProvider: Raw newQuantityValue for productId ${productId}:`, newQuantityValue, `(Type: ${typeof newQuantityValue})`);
                    const parsedQuantity = parseFloat(newQuantityValue);
                    console.log(`CartProvider: Parsed quantity for productId ${productId} (before validation/rounding):`, parsedQuantity);

                    // Ensure the new quantity is a valid number and at least 0.01.
                    let validatedQuantity = isNaN(parsedQuantity) || parsedQuantity <= 0 ? 0.01 : parsedQuantity;

                    // Round the quantity to avoid floating-point precision issues
                    validatedQuantity = roundToDecimals(validatedQuantity, 2); // Round to 2 decimal places

                    console.log(`CartProvider: Final validated and rounded quantity for productId ${productId}:`, validatedQuantity);
                    return { ...item, quantity: validatedQuantity };
                }
                return item;
            });
            // Filter out any items that might have had their quantity reduced to 0 or less
            const filteredCartItems = newCartItems.filter(item => item.quantity > 0);

            saveCartToLocalStorage(filteredCartItems);
            return filteredCartItems;
        });
    }, [saveCartToLocalStorage]);

    // NEW: Update the splitter value of an item in the cart
    const updateItemSplitter = useCallback((productId, splitterValue) => {
        setCartItems(currentCartItems => {
            const newCartItems = currentCartItems.map(item => {
                if (item.productId === productId) {
                    // Validate splitterValue: must be an integer between 1 and 9999
                    const parsedSplitter = parseInt(splitterValue, 10);
                    const validatedSplitter = Math.min(9999, Math.max(1, isNaN(parsedSplitter) ? 1 : parsedSplitter));
                    console.log(`CartProvider: Updating splitter for productId ${productId} to ${validatedSplitter}.`);
                    return { ...item, splitter: validatedSplitter };
                }
                return item;
            });
            saveCartToLocalStorage(newCartItems);
            return newCartItems;
        });
    }, [saveCartToLocalStorage]);

    // NEW: Add custom product to cart (ProductId = NULL)
    const addCustomProduct = useCallback((customProduct) => {
        setCartItems(currentCartItems => {
            // Generate unique temporary ID for custom product (negative numbers to distinguish from real ProductIds)
            const customProductId = -Date.now();
            
            const newCustomItem = {
                productId: customProductId, // Temporary negative ID
                quantity: customProduct.quantity || 1,
                splitter: 1,
                isCustom: true // Flag to identify custom products
            };
            
            console.log(`CartProvider: Adding custom product with temp ID ${customProductId}`);
            
            const newCartItems = [...currentCartItems, newCustomItem];
            saveCartToLocalStorage(newCartItems);
            
            // Also add to cartDetails directly (no backend fetch needed)
            setCartDetails(currentDetails => [
                ...currentDetails,
                {
                    productId: customProductId,
                    ProductId: null, // NULL for backend
                    ProductCode: "CustomProduct",
                    ProductDescription: customProduct.description || "",
                    RetailPrice: customProduct.price || 0,
                    quantity: customProduct.quantity || 1,
                    splitter: 1,
                    isCustom: true,
                    IVA_Porcentaje: customProduct.iva || 21 // Default IVA 21%
                }
            ]);
            
            return newCartItems;
        });
    }, [saveCartToLocalStorage]);

    // NEW: Update custom product fields (description, price, IVA)
    const updateCustomProduct = useCallback((customProductId, updatedFields) => {
        setCartDetails(currentDetails => {
            const newDetails = currentDetails.map(item => {
                if (item.productId === customProductId && item.isCustom) {
                    console.log(`CartProvider: Updating custom product ${customProductId}`, updatedFields);
                    return {
                        ...item,
                        ProductDescription: updatedFields.description !== undefined ? updatedFields.description : item.ProductDescription,
                        RetailPrice: updatedFields.price !== undefined ? updatedFields.price : item.RetailPrice,
                        IVA_Porcentaje: updatedFields.iva !== undefined ? updatedFields.iva : item.IVA_Porcentaje
                    };
                }
                return item;
            });
            return newDetails;
        });
    }, []);

    // --- Volume-Based Pricing Functions ---
    
    // Toggle volume pricing for a specific product
    const toggleVolumePrice = useCallback((productId) => {
        setVolumePricingStates(current => {
            const newStates = { ...current };
            const isCurrentlyActive = newStates[productId] || false;
            
            if (!isCurrentlyActive) {
                // Activating volume pricing
                newStates[productId] = true;
                
                // If quantity is above rangeValue2, reset to 1
                setCartItems(currentCartItems => {
                    const updatedCart = currentCartItems.map(item => {
                        if (item.productId === productId && item.quantity > volumePricingConfig.rangeValue2) {
                            console.log(`CartProvider: Volume pricing activated for productId ${productId}, resetting quantity from ${item.quantity} to 1.`);
                            return { ...item, quantity: 1 };
                        }
                        return item;
                    });
                    if (updatedCart !== currentCartItems) {
                        saveCartToLocalStorage(updatedCart);
                    }
                    return updatedCart;
                });
            } else {
                // Deactivating volume pricing
                delete newStates[productId];
            }
            
            console.log(`CartProvider: Volume pricing ${!isCurrentlyActive ? 'activated' : 'deactivated'} for productId ${productId}.`);
            saveVolumePricingToLocalStorage(newStates);
            return newStates;
        });
        setVolumePricingVersion(v => v + 1);
    }, [saveCartToLocalStorage, saveVolumePricingToLocalStorage, volumePricingConfig.rangeValue2]);
    
    // Get the effective price considering volume pricing with quantity-based tiers
    const getVolumePrice = useCallback((retailPrice, productId) => {
        const isVolumeActive = volumePricingStates[productId] || false;
        if (!isVolumeActive) {
            return retailPrice;
        }

        // Find the item to get its quantity
        const cartItem = cartItems.find(item => item.productId === productId);
        if (!cartItem) {
            return retailPrice;
        }

        const quantity = cartItem.quantity;
        const { rangeValue1, markValue1, rangeValue2, markValue2 } = volumePricingConfig;

        // Determine which tier applies based on quantity
        if (quantity >= 0.01 && quantity <= rangeValue1) {
            // Range 1: Apply markValue1
            const markup = markValue1 / 100;
            return retailPrice * (1 + markup);
        } else if (quantity >= (rangeValue1 + 1) && quantity <= rangeValue2) {
            // Range 2: Apply markValue2
            const markup = markValue2 / 100;
            return retailPrice * (1 + markup);
        } else {
            // More than rangeValue2: Return retail price (no markup)
            return retailPrice;
        }
    }, [volumePricingStates, cartItems, volumePricingConfig]);
    
    // Check if volume pricing is active for a product
    const isVolumePricingActive = useCallback((productId) => {
        return volumePricingStates[productId] || false;
    }, [volumePricingStates]);

    // Get volume pricing tier information for display purposes
    const getVolumePricingTier = useCallback((productId) => {
        const isVolumeActive = volumePricingStates[productId] || false;
        if (!isVolumeActive) {
            return null;
        }

        const cartItem = cartItems.find(item => item.productId === productId);
        if (!cartItem) {
            return null;
        }

        const quantity = cartItem.quantity;
        const { rangeValue1, markValue1, rangeValue2, markValue2 } = volumePricingConfig;

        if (quantity >= 0.01 && quantity <= rangeValue1) {
            return {
                tier: 1,
                markupPercent: markValue1,
                color: '#c41d1d', // Dark red for Tier 1 (RangeValue1)
                range: `0.01-${rangeValue1}`
            };
        } else if (quantity >= (rangeValue1 + 1) && quantity <= rangeValue2) {
            return {
                tier: 2,
                markupPercent: markValue2,
                color: '#d46a6a', // Matte red for Tier 2 (RangeValue2) - lighter than dark red
                range: `${rangeValue1 + 1}-${rangeValue2}`
            };
        } else {
            return {
                tier: 3,
                markupPercent: 0,
                color: '#e8a0a0', // Light red for Tier 3 (base value/no markup) - lighter than matte red
                range: `${rangeValue2 + 1}+`
            };
        }
    }, [volumePricingStates, cartItems, volumePricingConfig]);

    // --- Quantity Update with Volume Pricing Logic ---
    // Override the existing updateItemQuantity to handle volume pricing logic
    const updateItemQuantityWithVolumeLogic = useCallback((productId, newQuantityValue) => {
        const parsedQuantity = parseFloat(newQuantityValue);
        const validatedQuantity = isNaN(parsedQuantity) || parsedQuantity <= 0 ? 0.01 : roundToDecimals(parsedQuantity, 2);
        
        // If quantity goes above rangeValue2 and volume pricing is active, deactivate volume pricing
        if (validatedQuantity > volumePricingConfig.rangeValue2 && volumePricingStates[productId]) {
            console.log(`CartProvider: Quantity ${validatedQuantity} > ${volumePricingConfig.rangeValue2} for productId ${productId}, deactivating volume pricing.`);
            setVolumePricingStates(current => {
                const newStates = { ...current };
                delete newStates[productId];
                saveVolumePricingToLocalStorage(newStates);
                return newStates;
            });
        }
        
        // Call the original quantity update function
        updateItemQuantity(productId, validatedQuantity);
    }, [volumePricingStates, volumePricingConfig.rangeValue2, updateItemQuantity, saveVolumePricingToLocalStorage]);


    // Clear the entire cart (updated to clear discount too)
    const clearCart = useCallback(() => {
        console.log("CartProvider: Clearing cart and discount.");
        setCartItems([]);
        setCartDetails([]);
        setDiscountPercentageState(0);
        setVolumePricingStates({});
        localStorage.removeItem(LOCAL_STORAGE_CART_KEY);
        localStorage.removeItem(LOCAL_STORAGE_DISCOUNT_KEY);
        localStorage.removeItem(LOCAL_STORAGE_VOLUME_PRICING_KEY);
        window.dispatchEvent(new Event('cartStateUpdated'));
    }, []);

    // Wrapper for setCartDetails that also updates cartItems and localStorage
    const updateCartDetails = useCallback((newDetails) => {
        console.log("CartProvider: Updating cart details and syncing to cartItems:", newDetails);
        
        // Update cartDetails state
        setCartDetails(newDetails);
        
        // Convert cartDetails to cartItems format (productId, quantity, splitter)
        // Prefer lowercase productId (set for custom products with negative IDs) over ProductId
        const newCartItems = newDetails.map(detail => ({
            productId: detail.productId ?? detail.ProductId,
            quantity: roundToDecimals(detail.quantity || 1, 2),
            splitter: detail.splitter || 1,
            isCustom: detail.isCustom || false,
        }));
        
        // Update cartItems and save to localStorage
        setCartItems(newCartItems);
        saveCartToLocalStorage(newCartItems);
        
        // Dispatch event to update UI components
        window.dispatchEvent(new Event('cartStateUpdated'));
    }, [saveCartToLocalStorage]);

    // Setter for discount percentage (unchanged)
    const setDiscountPercentage = useCallback((percentage) => {
        const validatedPercentage = Math.min(100, Math.max(0, parseInt(percentage, 10) || 0));
        setDiscountPercentageState(validatedPercentage);
        saveDiscountToLocalStorage(validatedPercentage);
    }, [saveDiscountToLocalStorage]);


    // --- Context Value (updated) ---
    // Memoize the context value
    const contextValue = useMemo(() => ({
        cartItems,
        cartDetails,
        setCartDetails: updateCartDetails, // Use wrapper function instead of raw setter
        isLoadingCart,
        totalCartItemsCount,
        discountPercentage,
        subTotalPrice,
        totalAfterDiscount,
        getDiscountBreakdown, // NEW: Expose discount breakdown for display/validation
        toggleItemInCart,
        removeItemFromCart,
        updateItemQuantity: updateItemQuantityWithVolumeLogic, // Use the volume-aware version
        updateItemSplitter, // Expose new function
        addCustomProduct, // NEW: Custom product support
        updateCustomProduct, // NEW: Update custom product fields
        clearCart,
        setDiscountPercentage,
        getDisplayRetailPrice,
        getEffectivePrice, // GIULIANO: helper para precio efectivo segun toggle
        useCreditCardPrice, // GIULIANO: estado del switch
        setUseCreditCardPrice, // GIULIANO: setter del switch
        // Volume pricing functions
        toggleVolumePrice,
        getVolumePrice,
        isVolumePricingActive,
        getVolumePricingTier,
        clientDatabaseName, // Expose clientDatabaseName
        // V2 Calculation state (legacy - for backwards compatibility)
        v2Calculation,
        isCalculatingV2,
        v2Error,
        calculateCart, // Expose manual calculation function
        // DUAL CALCULATION (NEW) - Full dual calculation state
        dualCalculation
    }), [
        cartItems,
        cartDetails,
        updateCartDetails, // Use wrapper in dependencies
        isLoadingCart,
        totalCartItemsCount,
        discountPercentage,
        subTotalPrice,
        totalAfterDiscount,
        getDiscountBreakdown, // Add to dependencies
        toggleItemInCart,
        removeItemFromCart,
        updateItemQuantityWithVolumeLogic,
        updateItemSplitter, // Add to dependencies
        addCustomProduct, // Add to dependencies
        updateCustomProduct, // Add to dependencies
        clearCart,
        setDiscountPercentage,
        getDisplayRetailPrice,
        getEffectivePrice, // GIULIANO
        useCreditCardPrice, // GIULIANO
        setUseCreditCardPrice, // GIULIANO
        toggleVolumePrice,
        getVolumePrice,
        isVolumePricingActive,
        getVolumePricingTier,
        clientDatabaseName,
        // V2 dependencies (legacy compatibility)
        v2Calculation,
        isCalculatingV2,
        v2Error,
        calculateCart,
        // DUAL CALCULATION dependencies
        dualCalculation
    ]);

    // --- Render Provider ---
    return (
        <CartContext.Provider value={contextValue}>
            {children}
        </CartContext.Provider>
    );
};

// Add PropTypes for children
CartProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default CartProvider;